import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/useLocale";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from "@translations/config";

/**
 * Strip a known locale prefix from a pathname.
 *
 * Returns the underlying path (always starting with "/") so callers can
 * re-prefix it with whichever locale they want to switch to.
 */
function stripLocalePrefix(pathname: string): string {
  for (const loc of SUPPORTED_LOCALES) {
    if (loc === DEFAULT_LOCALE) continue;
    if (pathname === `/${loc}`) return "/";
    if (pathname.startsWith(`/${loc}/`)) return pathname.slice(`/${loc}`.length);
  }
  return pathname || "/";
}

function targetPathFor(pathname: string, target: SupportedLocale): string {
  const bare = stripLocalePrefix(pathname);
  if (target === DEFAULT_LOCALE) return bare;
  return bare === "/" ? `/${target}` : `/${target}${bare}`;
}

function rememberLocale(locale: SupportedLocale): void {
  if (typeof document === "undefined") return;
  document.cookie = `hm_locale=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

export function LocaleSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const active = useLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  const handleSwitch = (target: SupportedLocale) => {
    rememberLocale(target);
    if (target === active) return;
    const to = targetPathFor(pathname, target);
    void navigate({ to });
  };

  const baseLink = "mono text-[11px] uppercase tracking-[0.16em] transition-colors";

  return (
    <div
      role="group"
      aria-label="Language"
      className={"inline-flex items-center gap-2 " + (className ?? "")}
    >
      {SUPPORTED_LOCALES.map((loc, i) => {
        const isActive = loc === active;
        return (
          <span key={loc} className="inline-flex items-center gap-2">
            {i > 0 && (
              <span aria-hidden className="mono text-[11px] text-ink-3">
                ·
              </span>
            )}
            <button
              type="button"
              onClick={() => handleSwitch(loc)}
              aria-current={isActive ? "true" : undefined}
              aria-label={t(`common.switcher.${loc}`)}
              className={
                baseLink + " " + (isActive ? "font-semibold text-ink" : "text-ink-3 hover:text-ink")
              }
            >
              {t(`common.switcher.${loc}`)}
            </button>
          </span>
        );
      })}
    </div>
  );
}
